import React, { useRef, useState } from "react";
import { useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import defaultGuidelines from "./DefaultDescriptionsSow";
const fieldMappings = {
  ageOfMachine: "Age of Machine",
  ampereOfMachine: "Ampere of Machine",
  controllerSetting: "Controller Setting",
  subCategory: "Sub Category",
  type: "Machine Type",
  breadthFoot: "Breadth In Foot",
  lengthFoot: "Length In Foot",
};
const keyMachineLabels = {
  machinePrintingSubType: "Type Of Printer Head",
  picoliter: "Picoliter",
  machineBrand: "Brand",
  maxPrintSize: "Machine Width",
  ageOfMachine: "Machine Age",
};
const excludedFields = [
  "productType",
  "printMaterialbrand",
  "printGSM",
  "installationtableTop",
  "installationprojected",
  "installationceiling",
  "installation",
  "brand",
];
const keyPrintingLabels = {
  applicationPlace: "Application Place",
  artWork: "Artwork",
  installationfloor: "Installation Floor",
  installationwall: "Installation Wall",
  outerSize: "Outer Size",
  printMaterialgrade: "Print Material Grade",
  printMaterialsubstrate: "Print Material Substrate",
};

const ReportsMainModal = ({
  formData,
  enquiryId,
  setReportsModal,
  setFormData,
  operation,
  getRfpList,
  adminPortalView,
  setRfpGenerate,
  isRfpSerial,
}) => {
  const fabricationRef = useRef([]);
  const printingRef = useRef([]);
  const paintingRef = useRef([]);
  const token = localStorage.getItem("token");
  const serverURL = process.env.REACT_APP_URL;
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const updateBackend = async (updatedFormData) => {
    try {
      const putRes = await axios.put(
        `${serverURL}/requestForProposal/editRfp/${enquiryId}`,
        updatedFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (putRes.status === 200) {
        // optional: reset state from server’s version
        // setFormData(putRes.data.data);
      }
    } catch (err) {
      console.error("❌ Error saving:", err);
    }
  };

  const generateAndUploadPDF = async (element, namePrefix, product) => {
    if (!element) return;

    try {
      // Convert DOM node → image
      const dataUrl = await toPng(element, {
        cacheBust: true,
        useCORS: true,
        pixelRatio: 2, // ✅ doubles the resolution
      });
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 10;
      const marginY = 10;

      const img = new Image();
      img.src = dataUrl;

      await new Promise((resolve) => {
        img.onload = () => {
          const scale = Math.min(
            (pageWidth - 2 * marginX) / img.width,
            (pageHeight - 2 * marginY) / img.height,
            1,
          );

          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;

          pdf.addImage(
            dataUrl,
            "PNG",
            marginX,
            marginY,
            scaledWidth,
            scaledHeight,
          );
          resolve();
        };
      });

      // Convert to blob
      const pdfBlob = pdf.output("blob");
      const pdfFile = new File([pdfBlob], `${namePrefix}_${product + 1}.pdf`, {
        type: "application/pdf",
      });

      // Upload
      const uploadData = new FormData();
      uploadData.append("files", pdfFile);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/upload`,
        uploadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const uploadedUrl = response.data.data[0]; // PDF URL returned from upload

        // Now update backend
        setFormData((prev) => {
          const updated = {
            ...prev,
            rfpSerialNumber: isRfpSerial,
            reportsStatus: "latest",

            products: prev.products.map((ele) => {
              if (ele.productType === product.productType) {
                return {
                  ...ele,
                  ...(namePrefix === "fabrication"
                    ? { fabricationReport: uploadedUrl }
                    : namePrefix === "printing"
                      ? { printingReport: uploadedUrl }
                      : { paintingReport: uploadedUrl }),
                };
              }
              return ele;
            }),
          };

          // 👇 now call backend immediately with the fresh object
          updateBackend(updated);

          return updated;
        });
      } else {
        console.error(
          `❌ Upload failed for ${namePrefix} report ${product + 1}`,
        );
      }
    } catch (err) {
      console.error(
        `❌ Error uploading ${namePrefix} report ${product + 1}:`,
        err,
      );
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    const uploadAllReports = async () => {
      setIsUploading(true); // 🔥 start loading
      try {
        // Fabrication reports
        for (let i = 0; i < fabricationRef.current?.length; i++) {
          const entry = fabricationRef.current[i];
          if (!entry) continue;
          const { node, product } = entry;
          await generateAndUploadPDF(node, "fabrication", product);
        }

        // Printing reports
        for (let i = 0; i < printingRef.current?.length; i++) {
          const entry = printingRef.current[i];
          if (!entry) continue;
          const { node, product } = entry;
          await generateAndUploadPDF(node, "printing", product);
        }
        for (let i = 0; i < paintingRef.current?.length; i++) {
          const entry = paintingRef.current[i];
          if (!entry) continue;
          const { node, product } = entry;
          await generateAndUploadPDF(node, "painting", product);
        }

        Swal.fire({
          customClass: { container: "my-swal" },
          icon: "success",
          text: "Reports Generated Successfully",
          padding: "1em",
          color: "black",
          // background: "#000", // popup background
          backdrop: "rgba(0,0,0,1)", // full black overlay
        }).then(() => {
          if (adminPortalView) {
            setRfpGenerate(false);
            setReportsModal(false);
            getRfpList();
            navigate("/adminportal/enquiry");
          } else {
            navigate("/user/enquiry");
          }
        });
      } catch (err) {
        console.error("❌ Upload error", err);
      } finally {
        setIsUploading(false); // 🔥 stop loading
      }
    };

    uploadAllReports();
  }, []);

  return (
    <div className="flex bg-white  w-full justify-between items-start">
      <div>
        {isUploading && (
          <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black bg-opacity-90 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white/10 border border-white/20 p-8 rounded-2xl shadow-2xl text-center animate-pulse">
              <p className="text-xl font-bold text-white tracking-wide mb-6">
                Uploading Reports...
              </p>

              {/* Spinner */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="h-14 w-14 rounded-full border-4 border-t-transparent border-blue-400 animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-blue-300 font-semibold">PDF</span>
                  </div>
                </div>
              </div>

              {/* Animated dots */}
              <div className="flex justify-center mt-6 space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce delay-150"></div>
                <div className="w-3 h-3 bg-pink-400 rounded-full animate-bounce delay-300"></div>
              </div>

              {/* Subtext */}
              <p className="mt-6 text-gray-300 text-sm italic">
                Please don’t refresh or close this window
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="">
        {formData?.products?.map((reportData, index) => {
          const hasFabrication =
            reportData.servicesNeeded.includes("Fabrication");
          const hasPrinting = reportData.servicesNeeded.includes("Printing");
          const hasPainting = reportData.servicesNeeded.includes("Painting");

          return (
            <div key={`product-${index}`} className=" rounded-md p-3 m-2">
              {hasPrinting && (
                <div
                  className="p-6 border border-black-900 rounded-md"
                  ref={(el) => {
                    printingRef.current[index] = {
                      node: el,
                      product: reportData,
                    }; // ✅ store product too
                  }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex justify-center items-center">
                      <div>
                        <img
                          // src="/assests/AdminDashboard/pixellSowReportLogo.png"
                          src="/assests/images/pixell dark.png"
                          width={120}
                          height={120}
                          alt="logo"
                        />
                      </div>
                      <div className="mx-4">
                        {/* <h2 className="text-4xl font-bold">Pixell</h2> */}
                        <p className="text-2xl text-gray-500">
                          Enquiry Id: {formData?.rfpSerialNumber}
                        </p>
                        <p className="text-2xl text-gray-500">
                          support@pixell.in
                        </p>
                      </div>
                    </div>
                    <div className="text-start">
                      <h2 className="text-5xl text-black font-semibold">
                        Enquiry
                      </h2>
                    </div>
                  </div>
                  <div className=" flex justify-between border-t border-b border-black pt-4 pb-4">
                    <div className="border-l border-black px-4">
                      <h3 className="text-2xl font-semibold">Product:</h3>
                      <p className=" text-2xl">
                        <span className=" font-light">Product Category:</span>{" "}
                        {reportData?.productType}
                      </p>
                      <p className=" text-2xl">
                        <span className=" font-light">Service:</span> Printing
                      </p>
                    </div>
                  </div>

                  {reportData?.printingDetails && (
                    <div className=" w-full mt-5">
                      <h3 className="text-3xl font-semibold mb-3">
                        Printing Details
                      </h3>
                      <p className="border rounded-md p-3 text-[20px] break-words">
                        {reportData?.printingDetails
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p key={idx} className=" text-2xl">
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </p>
                    </div>
                  )}
                  <div className=" w-full mt-5">
                    <h3 className="text-3xl font-semibold mb-3">
                      Other Details
                    </h3>
                    <p className="border rounded-md p-3 text-[20px] break-words">
                      {reportData?.description
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p key={idx} className=" text-2xl">
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </p>
                  </div>

                  <div>
                    <section className="p-2">
                      <h3 className="text-3xl font-semibold my-3">
                        Printing Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.printing?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p key={idx} className="text-2xl">
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                    </section>
                    <section className="">
                      <h3 className="text-3xl font-semibold my-3">
                        PDI Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.pdi?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p key={idx} className="text-2xl">
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                    </section>
                    {/* <section className="p-2">
                      <h3 className="text-xl font-semibold my-3">
                        Packaging Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.packaging?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                      <section className="p-2">
                        <h3 className="text-xl font-semibold my-3">
                          Delivery Guidelines
                        </h3>
                        <div className="w-full p-2 border rounded-md">
                          {defaultGuidelines?.delivery?.detailsOfGuidelines
                            ?.split("•")
                            .filter((line) => line.trim() !== "")
                            .map((line, idx) => (
                              <p key={idx}>
                                {idx + 1}. {line.trim()}
                              </p>
                            ))}{" "}
                        </div>
                      </section>
                    </section> */}
                  </div>
                </div>
              )}
              {hasFabrication && (
                <div
                  className="p-6 border border-black-900 rounded-md mt-2"
                  ref={(el) => {
                    fabricationRef.current[index] = {
                      node: el,
                      product: reportData,
                    }; // ✅ store product too
                  }}
                >
                  <div className="flex justify-between items-center mb-2 h-[70px]">
                    <div className="flex justify-center items-center">
                      <div className="w-[100px] h-[40px]">
                        <img
                          src="/assests/images/pixell dark.png"
                          width={100}
                          height={55}
                          alt="logo"
                        />
                      </div>
                      <div className="mx-4">
                        {/* <h2 className="text-4xl font-bold">Pixell</h2> */}
                        <p className="text-2xl text-gray-500">
                          Enquiry Id: {formData?.rfpSerialNumber}
                        </p>
                        <p className="text-2xl text-gray-500">
                          support@pixell.in
                        </p>
                      </div>
                    </div>
                    <div className="text-start">
                      <h2 className="text-5xl text-black font-semibold">
                        Enquiry
                      </h2>
                    </div>
                  </div>
                  <div className=" flex justify-between border-t border-b border-black pt-4 pb-4">
                    <div className="border-l border-black px-4">
                      <h3 className="text-2xl font-semibold">Product:</h3>
                      <p className="text-2xl">
                        <span className="font-light">Product Category:</span>{" "}
                        {reportData?.productType}
                      </p>
                      <p className="text-2xl">
                        <span className="font-light">Service:</span> Fabrication
                      </p>
                    </div>
                  </div>

                  {reportData?.fabricationDetails && (
                    <div className=" w-full mt-5">
                      <h3 className="text-3xl font-semibold mb-3">
                        Fabrication Details
                      </h3>
                      <p className="border rounded-md p-3 text-[14px] break-words">
                        {reportData?.fabricationDetails
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-2xl" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </p>
                    </div>
                  )}
                  {reportData?.electricalDetails && (
                    <div className=" w-full mt-5">
                      <h3 className="text-3xl font-semibold mb-3">
                        Electrical Details
                      </h3>
                      <p className="border rounded-md p-3 text-[14px] break-words">
                        {reportData?.electricalDetails
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-2xl" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </p>
                    </div>
                  )}

                  <div className=" w-full mt-5">
                    <h3 className="text-3xl font-semibold mb-3">
                      Other Details
                    </h3>
                    <p className="border rounded-md p-3 text-[14px] break-words">
                      {reportData?.description
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p className="text-2xl" key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </p>
                  </div>

                  {/* <h3 className="text-lg font-semibold mt-3 ">
                    Fabrication Machines Required
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    {reportData?.machinesNeededForFabrication?.length === 0 ? (
                      <p className="border rounded-md p-3 text-[14px]">
                        No Machine Required
                      </p>
                    ) : (
                      reportData?.machinesNeededForFabrication?.map((i) => (
                        <div
                          key={i}
                          className="border shadow-md bg-white rounded-t-md"
                        >
                          <h4 className="font-semibold text-lg border-b py-1  bg-black px-4 text-white rounded-t-md">
                            {i?.type}
                          </h4>
                          <table className="w-full border-collapse border text-sm">
                            <tbody>
                              {i?.details &&
                                Object.entries(i.details).map(
                                  ([key, value], index) => {
                                    // Check if the field exists in the mapping; otherwise, fallback to key formatting
                                    const displayName =
                                      fieldMappings[key] ||
                                      key.replace(/([A-Z])/g, " $1");

                                    return (
                                      <tr
                                        key={index}
                                        className={
                                          index % 2 !== 0 ? "bg-gray-100" : ""
                                        }
                                      >
                                        <td className="border p-2">
                                          {index + 1}
                                        </td>
                                        <td className="border p-2">
                                          {displayName}
                                        </td>
                                        <td className="border p-2">{value}</td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        </div>
                      ))
                    )}
                  </div> */}
                  <section className="py-2">
                    <h1 className="my-2 text-3xl">Fabrication Guidelines</h1>
                    <div className="w-full p-2 border rounded-md">
                      {defaultGuidelines?.fabrication?.detailsOfGuidelines

                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p className="text-2xl" key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </div>
                  </section>
                  <section className="py-2">
                    <h1 className="my-2 text-3xl ">PDI Guidelines</h1>
                    <div className="w-full p-2 border rounded-md">
                      {defaultGuidelines?.pdi?.detailsOfGuidelines
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p className="text-2xl" key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </div>
                  </section>
                  {/* <section className="py-2">
                    <h1 className="my-2">Packaging Guidelines</h1>
                    <div className="w-full p-2 border rounded-md">
                      {defaultGuidelines?.packaging?.detailsOfGuidelines
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </div>
                  </section> */}
                  <section className="py-2">
                    <h1 className="my-2 text-3xl">Delivery Guidelines</h1>
                    <div className="w-full p-2 border rounded-md">
                      {defaultGuidelines?.delivery?.detailsOfGuidelines
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p className="text-2xl" key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}{" "}
                    </div>
                  </section>
                </div>
              )}

              {hasPainting && (
                <div
                  className="p-6 border border-black-900 rounded-md"
                  ref={(el) => {
                    paintingRef.current[index] = {
                      node: el,
                      product: reportData,
                    }; // ✅ store product too
                  }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex justify-center items-center">
                      <div>
                        <img
                          // src="/assests/AdminDashboard/pixellSowReportLogo.png"
                          src="/assests/images/pixell dark.png"
                          width={90}
                          height={45}
                          alt="logo"
                        />
                      </div>
                      <div className="mx-4">
                        {/* <h2 className="text-4xl font-bold">Pixell</h2> */}
                        <p className="text-[20px] text-gray-500">
                          Enquiry Id: {formData?.rfpSerialNumber}
                        </p>
                        <p className="text-[20px] text-gray-500">
                          support@pixell.in
                        </p>
                      </div>
                    </div>
                    <div className="text-start">
                      <h2 className="text-4xl text-black font-semibold">
                        Enquiry
                      </h2>
                    </div>
                  </div>
                  <div className=" flex justify-between border-t border-b border-black pt-4 pb-4">
                    <div className="border-l border-black px-4">
                      <h3 className="text-lg font-semibold">Product:</h3>
                      <p className="text-[20px]">
                        <span className=" font-light">Product Category:</span>{" "}
                        {reportData?.productType}
                      </p>
                      <p>
                        <span className="text-[20px] font-light">Service:</span>{" "}
                        Painting
                      </p>
                    </div>
                  </div>

                  <div className=" w-full mt-5">
                    <h3 className="text-xl font-semibold mb-3">
                      Other Details
                    </h3>
                    <p className="border rounded-md p-3 text-[14px] break-words">
                      {reportData?.description
                        ?.split("•")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <p key={idx}>
                            {idx + 1}. {line.trim()}
                          </p>
                        ))}
                    </p>
                  </div>
                  {reportData?.electricalDetails && (
                    <div className=" w-full mt-5">
                      <h3 className="text-xl font-semibold mb-3">
                        Electrical Details
                      </h3>
                      <p className="border rounded-md p-3 text-[14px] break-words">
                        {reportData?.electricalDetails
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-[20px]" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </p>
                    </div>
                  )}
                  {reportData?.printingDetails && (
                    <div className=" w-full mt-5">
                      <h3 className="text-xl font-semibold mb-3">
                        Printing Details
                      </h3>
                      <p className="border rounded-md p-3 text-[14px] break-words">
                        {reportData?.printingDetails
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-[20px]" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </p>
                    </div>
                  )}

                  <div>
                    <section className="p-2">
                      <h3 className="text-xl font-semibold my-3">
                        Printing Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.printing?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-[20px]" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                    </section>
                    <section className="p-2">
                      <h3 className="text-xl font-semibold my-3">
                        PDI Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.packaging?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-[20px]" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                    </section>
                    <section className="p-2">
                      <h3 className="text-xl font-semibold my-3">
                        Packaging Guidelines
                      </h3>
                      <div className="w-full p-2 border rounded-md">
                        {defaultGuidelines?.packaging?.detailsOfGuidelines
                          ?.split("•")
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => (
                            <p className="text-[20px]" key={idx}>
                              {idx + 1}. {line.trim()}
                            </p>
                          ))}
                      </div>
                      <section className="p-2">
                        <h3 className="text-xl font-semibold my-3">
                          Delivery Guidelines
                        </h3>
                        <div className="w-full p-2 border rounded-md">
                          {defaultGuidelines?.delivery?.detailsOfGuidelines
                            ?.split("•")
                            .filter((line) => line.trim() !== "")
                            .map((line, idx) => (
                              <p className="text-[20px]" key={idx}>
                                {idx + 1}. {line.trim()}
                              </p>
                            ))}{" "}
                        </div>
                      </section>
                    </section>
                  </div>
                  {/* <div className="mt-6">
              <h3 className="text-lg font-semibold ">
                Printing Machines Required
              </h3>
              <div className="grid grid-cols-2 gap-4 mt-2">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="border rounded-lg shadow-md bg-white">
                    <h4 className="font-semibold text-lg border-b pb-2 mb-2 bg-black p-4 text-white">
                      Welding Machine
                    </h4>
                    <div className="grid grid-cols-3 gap-4 p-4">
                      <div>
                        <p className="text-sm text-black font-semibold">
                          Sub Category
                        </p>
                        <p className="text-sm text-gray-500">Cat Name</p>
                      </div>
                      <div>
                        <p className="text-sm text-black font-semibold">
                          Ampere Of Machine
                        </p>
                        <p className="text-sm text-gray-500">30 A</p>
                      </div>
                      <div>
                        <p className="text-sm text-black font-semibold">
                          Controller Setting
                        </p>
                        <p className="text-sm text-gray-500">Setting</p>
                      </div>
                      <div>
                        <p className="text-sm text-black font-semibold">
                          Age Of Machine
                        </p>
                        <p className="text-sm text-gray-500">4 Years</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsMainModal;
